import config from 'Config/index';
import { useScriptLoader } from 'Src/hooks/use-script-loader/useScriptLoader';
import { useContext, useEffect, useState } from 'react';
import { PLEDG_PSP_ID } from 'Components/widgets/payment-info/PaymentInfo.const';
import { useFormikContext } from 'formik';
import type { PaymentFormValues } from 'Src/types/payment';
import { defaultGqlQueryOptions } from 'Src/types/payment';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { get } from 'lodash';
import { STATUS_CODE_400 } from 'Utils/status-code/statusCode.constants';
import { t } from 'Src/i18n';

const usePledgSignature = ({ price }, { ignore = false }) => {
  const formik = useFormikContext<PaymentFormValues>();
  const {
    bookingResume: { bookingId, proposalId, name },
    getPaymentErrorMessagePath,
  } = useContext(PaymentInfoContext);

  const track = useTracking();

  const { currentPayingCustomerId } = formik.values;

  const { data, isLoading, error } = useGQL<{ requestToken?: { token?: string } }>({
    query: `mutation { 
      requestToken(
        ${proposalId ? `proposalId: "${proposalId}",` : ''}
        ${bookingId ? `bookingId: "${bookingId}",` : ''}
        customerId: "${currentPayingCustomerId}",
        providerId: "${PLEDG_PSP_ID}",
        amount: ${price},
        name: "${t('booking.common.clubmed')} ${name}",
        currency: "EUR") {
          token
      }
    }`,
    cache: true,
    ignore,
    queryOptions: defaultGqlQueryOptions,
  });

  return {
    signature: data?.requestToken?.token,
    isLoading,
    error:
      !!error &&
      getPaymentErrorMessagePath({
        statusCode: get(error.errors, '[0].statusCode', STATUS_CODE_400),
        errorCode: get(error.errors[0], 'errorCode'),
        track,
      }),
  };
};

export const usePledg = ({
  price,
  onLoaded,
  shouldInit,
}: {
  price: number;
  onLoaded: (loaded: boolean) => void;
  shouldInit: boolean;
}) => {
  const scriptUrl = config.get('psp.pledg.script_url') as string;

  const [isPledgLoading, setIsPledgLoading] = useState(false);
  const [isPledgLoaded, setIsPledgLoaded] = useState(false);
  const formik = useFormikContext<PaymentFormValues>();

  const [pledgError, setPledgError] = useState('');

  const { signature, isLoading, error } = usePledgSignature(
    { price },
    { ignore: !shouldInit && !isPledgLoaded },
  );

  const handleRedirect = ({ purchase }) => {
    formik.setFieldValue('paymentId', purchase.reference);
    formik.submitForm();
  };

  const handleError = ({ error: e }) => {
    setPledgError(e?.message);
    formik.setFieldError('paymentId', '');
  };

  const { scriptLoaded } = useScriptLoader(scriptUrl);

  useEffect(() => {
    onLoaded(true);
  }, []);

  useEffect(() => {
    isLoading && setIsPledgLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    setIsPledgLoaded(signature && scriptLoaded);
  }, [signature, scriptLoaded]);

  useEffect(() => {
    if (signature && scriptUrl && scriptLoaded) {
      const button = document.getElementById('pledg-button');

      // eslint-disable-next-line no-new
      new window.Pledg!(button, {
        showCloseButton: false,
        signature,
        containerElement: document.querySelector('#pledg-funnel-container'),
        onSuccess: handleRedirect,
        onCheckValidity: handleError,
        onError: handleError,
      });

      button.click();
      setIsPledgLoading(false);
    }
  }, [signature, scriptLoaded]);

  return {
    isLoading: isPledgLoading,
    error: pledgError || error,
  };
};
